import { cn } from '@/utils/cn';
import { Icon } from '../Icon';
import type { IconName } from '../Icon';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AvatarSize = 'small' | 'regular' | 'medium' | 'large';

/**
 * Props for the Avatar component.
 *
 * `className` is intentionally omitted — Avatar is a sealed design system
 * component. Its dimensions, shape, and typography are fixed by design.
 * To add a new visual state, add a named prop rather than reaching for a
 * className override.
 */
export interface AvatarProps {
  /**
   * Image URL to display. When omitted the component renders `fallbackIcon`.
   */
  src?: string;
  /**
   * Icon to render when `src` is not provided.
   *
   * @default "user"
   */
  fallbackIcon?: IconName;
  /**
   * Controls the rendered dimensions.
   *
   * | Size     | Token     | px |
   * |----------|-----------|----|
   * | small    | `size-6`  | 32 |
   * | regular  | `size-7`  | 40 |
   * | medium   | `size-8`  | 48 |
   * | large    | `size-10` | 64 |
   *
   * @default "regular"
   */
  size?: AvatarSize;
  /**
   * When `true` the avatar is rendered as a full circle.
   * When `false` a small border-radius (`rounded-small`) is used instead.
   *
   * @default true
   */
  rounded?: boolean;
}

// ─── Size maps ────────────────────────────────────────────────────────────────

const sizeClassMap: Record<AvatarSize, string> = {
  small: 'size-6', // 32px — closest token to target 34px
  regular: 'size-7', // 40px — exact match
  medium: 'size-8', // 48px — closest token to target 50px
  large: 'size-10', // 64px — closest token to target 75px
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer Avatar component.
 *
 * A sealed, general-purpose avatar. Renders either:
 * 1. A provided image (`src` prop)
 * 2. A fallback icon (`fallbackIcon` prop, defaults to `"user"`) when no `src`
 *
 * This component is intentionally domain-agnostic — suitable for users,
 * products, brands, or any other entity. For user-specific behaviour
 * (initials fallback, CDN placeholder from a user ID) use `UserAvatar` instead.
 *
 * ### Sealed component
 * `className` is not exposed. Dimensions, shape, and the fallback icon are
 * controlled through named props.
 *
 * ### Sizes (closest Primer spacing tokens)
 * | Size    | px |
 * |---------|----|
 * | small   | 32 |
 * | regular | 40 |
 * | medium  | 48 |
 * | large   | 64 |
 *
 * @example
 * // Image
 * <Avatar src="https://example.com/product.jpg" size="regular" />
 *
 * @example
 * // Generic user icon fallback (default)
 * <Avatar size="medium" />
 *
 * @example
 * // Custom fallback icon for a non-user context
 * <Avatar fallbackIcon="tag" size="medium" rounded={false} />
 */
export function Avatar({
  src,
  fallbackIcon = 'user',
  size = 'regular',
  rounded = true,
}: AvatarProps) {
  const sizeClass = sizeClassMap[size];
  const shapeClass = rounded ? 'rounded-full' : 'rounded-small';

  const containerClass = cn(
    'relative flex items-center justify-center overflow-hidden bg-media flex-shrink-0',
    sizeClass,
    shapeClass
  );

  if (src) {
    return (
      <div className={containerClass}>
        <img src={src} alt="" className="object-cover w-full h-full" />
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <Icon name={fallbackIcon} color="white" size="large" aria-hidden="true" />
    </div>
  );
}

export default Avatar;
